import React, { useEffect } from "react";
import Button, { ButtonProps } from "../../core/button/Button";
import { cn } from "../../../util/classNameConstructor";

export function getPagination(
  currentPage: number,
  pages: number[],
  maxLength: number,
  ellipsis = "..."
): (number | string)[] {
  const halfMaxLength = Math.floor((maxLength - 2) / 2);
  const firstPage = 1;
  const lastPage = pages.length;

  if (pages.length <= maxLength) return pages;

  if (currentPage <= halfMaxLength + 1) {
    const visiblePages = pages.slice(0, maxLength - 2);
    return [...visiblePages, ellipsis, lastPage];
  }

  if (currentPage >= lastPage - halfMaxLength) {
    const visiblePages = pages.slice(lastPage - maxLength + 2);
    return [firstPage, ellipsis, ...visiblePages];
  }

  const visiblePages = pages.slice(
    currentPage - halfMaxLength + 1,
    currentPage + halfMaxLength - 1
  );
  return [firstPage, ellipsis, ...visiblePages, ellipsis, lastPage];
}
interface ItemProps extends ButtonProps {
  active: string;
}
const Item = ({ active, ...props }: ItemProps) => {
  return (
    <Button
      variant={active === props.text ? "outline" : "shapeless"}
      className={cn("w-9", active === props.text ? "pointer-events-none" : "")}
      pillShaped={false}
      {...props}
      // text={Number.isNaN(props.text) ? Number(props.text) : Number(props.text)}
    />
  );
};
interface TableFooterProps {
  range: number[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  slice: any[];
  showPages?: number;
}
const TableFooter = ({
  range,
  setPage,
  page,
  slice,
  showPages = 10,
}: TableFooterProps) => {
  const lastPage = range[range.length - 1];
  useEffect(() => {
    if (slice.length < 1 && page !== 1) {
      setPage(page - 1);
    }
  }, [slice, page, setPage]);

  return (
    <div className="z-50 m-2 flex items-center">
      <Item
        leadingIcon="arrowhead-left-md"
        active={"0"}
        disabled={page === 1}
        disabledNotice="You are on the first page."
        onClick={() => setPage((e) => Math.max(e - 1, 1))}
      />
      {getPagination(page, range, Math.max(showPages, 6)).map((p, i, arr) => {
        let jumpTo = Number(p);
        if (p === "...") {
          const prev = Number(arr[i - 1]);
          const next = Number(arr[i + 1]);
          jumpTo = Math.floor(prev + (next - prev) / 2);
        }

        return (
          <Item
            key={i}
            active={page.toString()}
            text={p.toString()}
            onClick={() => setPage(jumpTo)}
            aria-label={`Go to page ${jumpTo}`}
          />
        );
      })}
      <Item
        leadingIcon="arrowhead-right-md"
        active={"0"}
        disabled={page === lastPage}
        disabledNotice="You are on the last page."
        onClick={() => setPage((e) => Math.min(e + 1, lastPage))}
      />
    </div>
  );
};

export default TableFooter;
